import { get, post, put, del } from '../client/axiosClient';
import { timesheetEndpoints } from '../endpoints/timesheet.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, PaginatedResponse, EmptyResponse, ID,
  Timesheet, CreateTimesheetRequest, UpdateTimesheetRequest, TimesheetSearchRequest,
  ReopenTimesheetRequest,
} from '../types';

class TimesheetService {
  async getAll(params?: TimesheetSearchRequest): Promise<PaginatedResponse<Timesheet>> {
    const endpoint = params
      ? buildEndpointWithQuery(timesheetEndpoints.basePath(), params as Record<string, any>)
      : timesheetEndpoints.list();
    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Timesheet>> {
    const response = await get<ApiResponse<Timesheet>>(timesheetEndpoints.getById(id));
    return response.data;
  }

  async getByEmployeeWeek(employeeId: string, weekStartDate: string): Promise<ApiResponse<Timesheet>> {
    const response = await get<ApiResponse<Timesheet>>(
      timesheetEndpoints.getByEmployeeWeek(employeeId, weekStartDate)
    );
    return response.data;
  }

  async create(data: CreateTimesheetRequest): Promise<ApiResponse<Timesheet>> {
    const response = await post<ApiResponse<Timesheet>>(timesheetEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateTimesheetRequest): Promise<ApiResponse<Timesheet>> {
    const response = await put<ApiResponse<Timesheet>>(timesheetEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(timesheetEndpoints.delete(id));
    return { success: true };
  }

  async reopen(id: ID, data: ReopenTimesheetRequest): Promise<ApiResponse<Timesheet>> {
    const response = await post<ApiResponse<Timesheet>>(timesheetEndpoints.reopen(id), data);
    return response.data;
  }
}

export const timesheetService = new TimesheetService();
export default timesheetService;
